/**
 * Thin fetch client for the IndStartups FastAPI backend.
 *
 * - In dev, Vite proxies `/api/*` → http://127.0.0.1:8787 so we stay same-origin.
 * - In prod, Cloudflare Pages can't rewrite cross-origin via `_redirects`,
 *   so we call the API hostname directly. The backend's CORS middleware is
 *   already configured to allow https://ind-startups.aisarva.com.
 *
 * Override for local-against-prod-api testing: `VITE_API_BASE=https://…`.
 */
function resolveApiBase(): string {
  const explicit = (import.meta.env.VITE_API_BASE as string | undefined) || "";
  if (explicit) return explicit;
  if (typeof window !== "undefined"
      && /(^|\.)ind-startups\.aisarva\.com$/.test(window.location.hostname)) {
    return "https://api-ind-startups.aisarva.com";
  }
  return ""; // dev — Vite proxies /api
}
const API_BASE: string = resolveApiBase();

function url(path: string): string { return `${API_BASE}${path}`; }

export interface Scheme {
  id: string;
  source: string;
  slug?: string | null;
  name: string;
  short_title?: string | null;
  category?: string | null;
  ministry?: string | null;
  level?: string | null;
  state?: string | null;
  summary?: string | null;
  benefits?: string | null;
  eligibility?: string | null;
  how_to_apply?: string | null;
  documents?: string | null;
  benefit_type?: string | null;
  audience?: string | null;
  tags?: string[];
  application_url?: string | null;
  source_url?: string | null;
}

export interface SearchResult {
  total: number;
  items: Scheme[];
}

export interface FacetEntry { value: string; count: number; }
export interface Facets {
  level:        FacetEntry[];
  state:        FacetEntry[];
  audience:     FacetEntry[];
  benefit_type: FacetEntry[];
  source:       FacetEntry[];
}

export interface StateRow {
  state: string;
  code?: string;
  total: number;
  orgs: number;
  schemes: number;
  loans: number;
  subsidies: number;
  orgs_sample: Array<{
    id: string;
    name: string;
    short_title?: string | null;
    category?: string | null;
    application_url?: string | null;
  }>;
}

export interface JourneyStep {
  icon: string;
  title: string;
  summary: string;
  url?: string;
  scheme?: string;
}
export interface JourneyTrack {
  id: string;
  title: string;
  tagline: string;
  accent: "saffron" | "accent" | "ink";
  steps: JourneyStep[];
}

export interface Stats {
  total: number;
  states_covered: number;
  by_audience: Record<string, number>;
  by_benefit:  Record<string, number>;
  ecosystem_headlines: Array<{ label: string; value: string }>;
}

export interface ChatMessage { role: "user" | "assistant" | "system"; content: string; }

async function json<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url(path), init);
  if (!r.ok) throw new Error(`${path} -> HTTP ${r.status}`);
  return r.json() as Promise<T>;
}

export const api = {
  health:    () => json<{ ok: boolean; schemes: number }>("/api/health"),
  stats:     () => json<Stats>("/api/stats"),
  filters:   () => json<Facets>("/api/filters"),
  journeys:  () => json<{ tracks: JourneyTrack[] }>("/api/journeys"),
  states:    () => json<{ items: StateRow[] }>("/api/states"),
  scheme:    (id: string) => json<Scheme>(`/api/scheme/${encodeURIComponent(id)}`),
  search:    (params: Record<string, string | number | undefined>) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "" && v !== 0) p.set(k, String(v));
    }
    return json<SearchResult>(`/api/search?${p.toString()}`);
  },
  recommend: (params: Record<string, string | undefined>) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) p.set(k, v);
    return json<{ items: Scheme[] }>(`/api/recommend?${p.toString()}`);
  },
  chatStream: (messages: ChatMessage[]) =>
    fetch(url("/api/chat"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages }),
    }),
};
